import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import axios from 'axios'
import {
    useFiatCurrencies,
    useCryptocurrencies,
} from '@/hooks/useExchangeRates'
import { AddCurrencyModal } from './AddCurrencyModal'
import {
    BTCIcon,
    DOGEIcon,
    USDTIcon,
    ETHIcon,
    USDCIcon,
    TRONIcon,
} from './CryptoIcons'
import type { FiatCurrency } from '@/types/exchange-rate'

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as {
            message?: string
            errors?: string[]
        }
        if (data?.errors?.length) return data.errors.join(', ')
        return data?.message || error.message
    }
    return error instanceof Error
        ? error.message
        : 'An unexpected error occurred'
}

const getCryptoIcon = (symbol: string) => {
    switch (symbol.toUpperCase()) {
        case 'BTC':
            return <BTCIcon />
        case 'USDT':
            return <USDTIcon />
        case 'USDC':
            return <USDCIcon />
        case 'DOGE':
            return <DOGEIcon />
        case 'ETH':
            return <ETHIcon />
        case 'TRON':
            return <TRONIcon />
        default:
            return null
    }
}

export const ExchangeRatesTab = () => {
    const {
        data: fiatCurrencies,
        isLoading: isLoadingFiats,
        error: fiatsError,
        refetch: refetchFiats,
    } = useFiatCurrencies()

    const {
        data: cryptocurrencies,
        isLoading: isLoadingCryptos,
        error: cryptosError,
    } = useCryptocurrencies()

    const [isAddCurrencyModalOpen, setIsAddCurrencyModalOpen] = useState(false)

    if (fiatsError || cryptosError) {
        return (
            <div className="flex h-48 flex-col items-center justify-center gap-4">
                <p className="text-sm text-destructive">
                    {getErrorMessage(fiatsError || cryptosError)}
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchFiats()}
                    className="gap-2"
                >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                </Button>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Fiat Currencies Section */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                    <h2 className="text-base font-medium text-white">
                        Fiat currencies
                    </h2>
                </div>

                <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                        Supported fiat currencies on the platform
                    </p>
                    {/* <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsAddCurrencyModalOpen(true)}
                        className="gap-2 text-[#17A2B8] hover:text-[#1493a7]"
                    >
                        <Plus className="h-4 w-4" />
                        Add currency
                    </Button> */}
                </div>

                {isLoadingFiats ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton
                                key={i}
                                className="h-14 w-full rounded-xl"
                            />
                        ))}
                    </div>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {fiatCurrencies?.map((fiat: FiatCurrency) => (
                            <div
                                key={fiat.id}
                                className="flex items-center gap-3 rounded-xl border bg-white px-4 py-3"
                            >
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-base font-semibold">
                                    {fiat.symbol}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground">
                                        {fiat.code}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {fiat.name}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Cryptocurrencies Section */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                    <h2 className="text-base font-medium text-white">
                        Supported cryptocurrencies
                    </h2>
                </div>

                {isLoadingCryptos ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton
                                key={i}
                                className="h-14 w-full rounded-xl"
                            />
                        ))}
                    </div>
                ) : cryptocurrencies?.length === 0 ? (
                    <div className="flex min-h-[120px] items-center justify-center rounded-xl border-2 border-dashed border-muted-foreground/20 bg-muted/10">
                        <p className="text-sm text-muted-foreground">
                            No cryptocurrencies found
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {cryptocurrencies?.map(crypto => (
                            <div
                                key={crypto.id}
                                className="flex items-center gap-3 rounded-xl border bg-white px-4 py-3"
                            >
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
                                    {getCryptoIcon(crypto.symbol) ?? (
                                        <span className="text-xs font-bold">
                                            {crypto.symbol.slice(0, 2)}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-foreground">
                                        {crypto.symbol}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {crypto.name}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Currency Modal */}
            <AddCurrencyModal
                open={isAddCurrencyModalOpen}
                onOpenChange={setIsAddCurrencyModalOpen}
                availableCurrencies={fiatCurrencies || []}
                onSave={enabledCurrencies => {
                    console.log('Enabled currencies:', enabledCurrencies)
                    setIsAddCurrencyModalOpen(false)
                    toast.success('Currency preferences updated')
                }}
            />
        </div>
    )
}
